// Stores Service - Realtime Database operations for Tiendas
import { ref, get, update, remove, set, onValue, off } from 'firebase/database'
import { database } from './firebase'
import {
  HIGH_IMPACT_DELETIONS_DISABLED,
  getHighImpactDeletionBlock,
} from '@/utils/highImpactDeletionGuard'

// Base path for stores in Firebase Realtime Database
const STORES_BASE_PATH = 'projects/superkomprasBackoffice/stores'

// Helper para obtener timestamp en milisegundos
const getTimestamp = () => Date.now()

/**
 * Get all stores from Realtime Database
 * @returns {Promise<Object>} All stores data
 */
export const getAllStores = async () => {
  try {
    const storesRef = ref(database, STORES_BASE_PATH)
    const snapshot = await get(storesRef)

    if (snapshot.exists()) {
      const storesData = snapshot.val()
      // Convert object to array with store id as key
      const storesArray = Object.keys(storesData).map(key => ({
        id: key,
        ...storesData[key],
      }))

      return {
        success: true,
        data: storesArray,
      }
    } else {
      return {
        success: true,
        data: [],
      }
    }
  } catch (error) {
    console.error('Error in getAllStores:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener tiendas',
      data: [],
    }
  }
}

/**
 * Get store by ID from Realtime Database
 * @param {string} storeId - Store ID (key)
 * @returns {Promise<Object>} Store data or null if not found
 */
export const getStoreById = async storeId => {
  try {
    const storeRef = ref(database, `${STORES_BASE_PATH}/${storeId}`)
    const snapshot = await get(storeRef)

    if (snapshot.exists()) {
      return {
        success: true,
        data: {
          id: storeId,
          ...snapshot.val(),
        },
      }
    } else {
      return {
        success: false,
        error: 'Tienda no encontrada',
        data: null,
      }
    }
  } catch (error) {
    console.error('Error in getStoreById:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener tienda',
      data: null,
    }
  }
}

/**
 * Create a new store in Realtime Database
 * @param {Object} storeData - Store data
 * @param {string} storeData.direccion - Store address
 * @param {string} storeData.division - Division code
 * @param {number} storeData.latitude - Latitude
 * @param {number} storeData.longitude - Longitude
 * @param {string} storeData.sucursal - Store name
 * @param {string} storeData.telefono - Phone number
 * @returns {Promise<Object>} Result of create operation
 */
export const createStore = async storeData => {
  try {
    // Validate required fields
    if (!storeData.sucursal || !storeData.sucursal.trim()) {
      return {
        success: false,
        error: 'El nombre de la sucursal es requerido',
      }
    }

    if (!storeData.division || !storeData.division.trim()) {
      return {
        success: false,
        error: 'La división es requerida',
      }
    }

    const divisionKey = storeData.division.trim()

    // Prepare store data with timestamps
    const pickingConfig =
      storeData.pickingConfig && typeof storeData.pickingConfig === 'object'
        ? storeData.pickingConfig
        : {}

    const newStoreData = {
      direccion: storeData.direccion || '',
      division: divisionKey,
      latitude: storeData.latitude || 0,
      longitude: storeData.longitude || 0,
      sucursal: storeData.sucursal.trim(),
      telefono: storeData.telefono || '',
      pickingScannerEnabled: storeData.pickingScannerEnabled === true,
      ...(Object.keys(pickingConfig).length ? { pickingConfig } : {}),
      createdAt: getTimestamp(),
    }

    // Prevent overriding existing stores with the same division
    const storeRef = ref(database, `${STORES_BASE_PATH}/${divisionKey}`)
    const existingStoreSnapshot = await get(storeRef)

    if (existingStoreSnapshot.exists()) {
      return {
        success: false,
        error: 'Ya existe una tienda registrada con esa división',
      }
    }

    await set(storeRef, newStoreData)

    return {
      success: true,
      data: {
        id: divisionKey,
        ...newStoreData,
      },
      message: 'Tienda creada correctamente',
    }
  } catch (error) {
    console.error('Error in createStore:', error)
    return {
      success: false,
      error: error.message || 'Error al crear tienda',
    }
  }
}

/**
 * Update a store in Realtime Database
 * @param {string} storeId - Store ID (key)
 * @param {Object} storeData - Store data to update
 * @returns {Promise<Object>} Result of update operation
 */
export const updateStore = async (storeId, storeData) => {
  try {
    // Validate required fields
    if (!storeData.sucursal || !storeData.sucursal.trim()) {
      return {
        success: false,
        error: 'El nombre de la sucursal es requerido',
      }
    }

    if (!storeData.division || !storeData.division.trim()) {
      return {
        success: false,
        error: 'La división es requerida',
      }
    }

    const divisionKey = storeData.division.trim()

    // Prepare update data
    const updateData = {
      direccion: storeData.direccion || '',
      division: divisionKey,
      latitude: storeData.latitude || 0,
      longitude: storeData.longitude || 0,
      sucursal: storeData.sucursal.trim(),
      telefono: storeData.telefono || '',
      pickingScannerEnabled: storeData.pickingScannerEnabled === true,
      updatedAt: getTimestamp(),
    }

    const currentStoreRef = ref(database, `${STORES_BASE_PATH}/${storeId}`)
    const currentSnapshot = await get(currentStoreRef)

    if (!currentSnapshot.exists()) {
      return {
        success: false,
        error: 'Tienda no encontrada',
      }
    }

    const currentData = currentSnapshot.val() || {}

    const incomingPc =
      storeData.pickingConfig && typeof storeData.pickingConfig === 'object'
        ? storeData.pickingConfig
        : null
    if (incomingPc) {
      const mergedPc = {
        ...(currentData.pickingConfig && typeof currentData.pickingConfig === 'object'
          ? currentData.pickingConfig
          : {}),
        ...incomingPc,
      }
      updateData.pickingConfig = mergedPc
    }

    if (divisionKey === storeId) {
      await update(currentStoreRef, updateData)

      return {
        success: true,
        data: {
          id: storeId,
          ...currentData,
          ...updateData,
        },
        message: 'Tienda actualizada correctamente',
      }
    }

    const newStoreRef = ref(database, `${STORES_BASE_PATH}/${divisionKey}`)
    const divisionSnapshot = await get(newStoreRef)

    if (divisionSnapshot.exists()) {
      return {
        success: false,
        error: 'Ya existe una tienda registrada con esa división',
      }
    }

    const dataToPersist = {
      ...currentData,
      ...updateData,
    }

    await set(newStoreRef, dataToPersist)
    await remove(currentStoreRef)

    return {
      success: true,
      data: {
        id: divisionKey,
        ...dataToPersist,
      },
      message: 'Tienda actualizada correctamente',
    }
  } catch (error) {
    console.error('Error in updateStore:', error)
    return {
      success: false,
      error: error.message || 'Error al actualizar tienda',
    }
  }
}

/**
 * Delete a store from Realtime Database
 * @param {string} storeId - Store ID (key)
 * @returns {Promise<Object>} Result of delete operation
 */
export const deleteStore = async storeId => {
  try {
    if (HIGH_IMPACT_DELETIONS_DISABLED) {
      return getHighImpactDeletionBlock('eliminar tienda')
    }

    const storeRef = ref(database, `${STORES_BASE_PATH}/${storeId}`)
    await remove(storeRef)

    return {
      success: true,
      message: 'Tienda eliminada correctamente',
    }
  } catch (error) {
    console.error('Error in deleteStore:', error)
    return {
      success: false,
      error: error.message || 'Error al eliminar tienda',
    }
  }
}

/**
 * Subscribe to stores changes in real-time
 * @param {Function} callback - Callback function to handle changes
 * @returns {Function} Unsubscribe function
 */
export const subscribeToStores = callback => {
  const storesRef = ref(database, STORES_BASE_PATH)

  const handler = snapshot => {
    if (snapshot.exists()) {
      const storesData = snapshot.val()
      const storesArray = Object.keys(storesData).map(key => ({
        id: key,
        ...storesData[key],
      }))
      callback({ success: true, data: storesArray })
    } else {
      callback({ success: true, data: [] })
    }
  }

  onValue(storesRef, handler)

  // Return unsubscribe function
  return () => {
    off(storesRef, handler)
  }
}

export default {
  getAllStores,
  getStoreById,
  createStore,
  updateStore,
  deleteStore,
  subscribeToStores,
}
